'use client';
/**
 * RELANCE DE LECTURE SUR UNE SÉLECTION DE DOCUMENTS (MAN-5).
 *
 * ── Le geste, en deux temps, et pourquoi jamais en un ──────────────────────
 *
 * Cette commande envoie des courriels à des collègues. Elle ne part donc
 * JAMAIS d'un clic : on demande d'abord un aperçu — une route qui ne fait que
 * lire —, on affiche nominativement ce qui partirait, et l'envoi n'a lieu qu'au
 * second clic, sur un bouton qui annonce le nombre exact.
 *
 * ⚠️ CE QUI EST MONTRÉ EST COMPTÉ PAR PERSONNE. « 48 relances » paraît
 * raisonnable ; c'est peut-être neuf courriels à la même personne. La plus
 * sollicitée est en tête de liste, avec les documents qui la concernent : c'est
 * sur elle que porte réellement la décision d'envoyer ou non.
 *
 * ⚠️ ET CE QUI NE PARTIRA PAS EST DIT AUSSI — les lecteurs sans adresse, et
 * ceux qu'on ignore parce qu'ils ont été relancés il y a moins de 24 h. Taire
 * une personne sans adresse produirait une trace d'audit qui affirme un envoi
 * qui n'a pas eu lieu.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { Modale, I } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
type Personne = { cle: string; nom: string; documents: string[]; sansAdresse: boolean };
type Apercu = {
  personnes: Personne[];
  courriels: number;
  documentsTouches: number;
  ignores: number;
  sansAdresse: number;
  phrase: string;
};

export function RelanceLot({ ids, onFerme }: { ids: string[]; onFerme: (envoye: boolean) => void }) {
  const { interroger, smqData, showToast } = useGed();
  const [apercu, setApercu] = React.useState<Apercu | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [envoiEnCours, setEnvoiEnCours] = React.useState(false);

  React.useEffect(() => {
    let vivant = true;
    void (async () => {
      const r = await interroger<Apercu & { ok?: boolean; error?: string }>('ged/relance-lot/apercu', { ids });
      if (!vivant) return;
      if (!r) { setErreur('L’aperçu n’a pas pu être obtenu.'); return; }
      setApercu(r);
    })();
    return () => { vivant = false; };
  }, [ids, interroger]);

  const rienAEnvoyer = !!apercu && apercu.courriels === 0;

  return (
    <Modale
      titre="Relancer les accusés de lecture"
      sousTitre={`${ids.length} document(s) sélectionné(s)`}
      onClose={() => onFerme(false)}
      largeur={620}
      pied={(
        <>
          <Bouton taille="sm" onClick={() => onFerme(false)}>Annuler</Bouton>
          {/* ⚠️ LE BOUTON PORTE LE NOMBRE. « Envoyer » tout court se clique sans
              relire ; « Envoyer 4 courriels » oblige à voir ce qu'on déclenche.
              Et tant que l'aperçu n'est pas là, il n'y a rien à confirmer :
              le bouton n'existe pas encore. */}
          {apercu && !rienAEnvoyer && (
            <Bouton variante="primaire" disabled={envoiEnCours} onClick={() => void (async () => {
                setEnvoiEnCours(true);
                const r = await smqData<{ remis?: number; echecs?: number; sansAdresse?: number }>(
                  'ged/relance-lot', { ids }, undefined, 'relance groupée');
                setEnvoiEnCours(false);
                if (!r) return;
                // On rapporte CE QUI EST PARTI, pas ce qu'on avait prévu.
                const bouts = [`${r.remis ?? 0} courriel(s) remis`];
                if (r.echecs) bouts.push(`${r.echecs} en échec d’envoi`);
                if (r.sansAdresse) bouts.push(`${r.sansAdresse} sans adresse`);
                showToast(`Relance groupée — ${bouts.join(', ')}.`);
                onFerme(true);
              })()}>
              <I n="send" s={14} />
              {envoiEnCours ? 'Envoi…' : `Envoyer ${apercu.courriels} courriel(s)`}
            </Bouton>
          )}
        </>
      )}>
      {erreur && <p style={{ margin: 0, fontSize: 13, color: 'var(--red-600)' }}>{erreur}</p>}
      {!apercu && !erreur && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Calcul de ce qui partirait…</p>}
      {apercu && (
        <div style={{ display: 'grid', gap: 14 }}>
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-body)' }}>{apercu.phrase}</p>

          {apercu.personnes.length > 0 && (
            <div style={{ display: 'grid', gap: 7 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
                Qui recevra quoi
              </div>
              {apercu.personnes.map((p) => (
                <div key={p.cle} style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', paddingBottom: 6, borderBottom: '1px solid var(--border-subtle)' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: p.sansAdresse ? 'var(--text-muted)' : 'var(--text-strong)' }}>{p.nom}</span>
                  {p.sansAdresse
                    ? (
                      <span title="Aucune adresse courriel connue : rien ne partira pour cette personne"
                        style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '1px 8px', background: 'var(--amber-50)', color: 'var(--amber-600, #b45309)' }}>
                        sans adresse — rien ne partira
                      </span>
                    )
                    : <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{p.documents.length} courriel(s)</span>}
                  <span style={{ flex: '1 1 100%', fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {p.documents.join(' · ')}
                  </span>
                </div>
              ))}
            </div>
          )}

          {rienAEnvoyer && (
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-muted)' }}>
              Rien à envoyer sur cette sélection. Ce n’est pas une anomalie : c’est le cas quand
              tout le monde a déjà accusé réception, ou quand les lecteurs concernés ont été
              relancés récemment.
            </p>
          )}
        </div>
      )}
    </Modale>
  );
}
